<template>
  <div class="dean-table">
    <table class="dean-matrix" v-if="rows.length > 0">
      <thead>
      <tr>
        <td class="corner"></td>
        <th v-for="column in columns">{{column}}</th>
      </tr>
      </thead>
      <tr v-for="(row, rowIndex) in rows">
        <th class="row">{{row}}</th>
        <td v-for="(entry, entryIndex) in data[rowIndex]" class="link" @click="onClick(entryIndex, rowIndex)">{{entry.label}} <dean-icon width="13" height="13" :glyph="getIcon(entry.icon)"/></td>
      </tr>
    </table>
    <div class="no-data" v-else>
      <p class="no-data-text">Geen resultaten...</p>
      <dean-icon
          class="no-data-icon"
          :glyph="archiveIcon">
      </dean-icon>
    </div>
  </div>
</template>

<!-- SCRIPT -->
<script lang="ts">
  // VUE
  import Vue from 'vue';
  import {Component, Prop} from 'vue-property-decorator';
  import DeanIcon from '@/components/deanComponents/deanIcon.vue';

  import {editIcon, plusIcon, archiveIcon} from '@deanone/dean-components'

  @Component({
    components: {
      DeanIcon,
    },
  })
  export default class DeanMatrix extends Vue {
    @Prop() public data!: Array<Array<any>>;
    @Prop() public rows!: string[];
    @Prop() public columns!: string[];
    public archiveIcon: object = archiveIcon;

    public getIcon(iconString: string): object {
      return iconString === 'edit' ? editIcon : plusIcon;
    }

    public onClick(columnIndex: number, rowIndex: number) {
      this.$emit('click', [rowIndex, columnIndex]);
    }
  }
</script>

<style lang="scss" scoped>
  // IMPORT
  @import '~@deanone/dean-components/src/scss/vars';

  .no-data {
    @include display-flex();
    justify-content: center;
    flex-direction: column;
    align-items: center;
    width: 100%;
    font-weight: 700;
    color: var(--color-light-shade-1, $color-light-shade-1);
    padding: 20px;
    .no-data-text {
      text-align: center;
      width: 100%;
      display: block;
      font-size: 20pt;
      line-height: 1;
      padding-bottom: 20px;
      word-break: keep-all;
      @media #{$medium} {
        font-size: 30pt;
      }
    }
    .no-data-icon {
      @include display-flex();
      justify-content: center;
      width: 80%;
      fill: var(--color-light-shade-2, $color-light-shade-2);
      @media #{$medium} {
        width: 15%;
      }
    }
  }

  // DEAN TABLE
  .dean-table {
    padding: 10px;

    table {
      border-collapse: collapse;
      margin: 0;
      padding: 0;
      width: 100%;
      overflow-wrap: break-word;
      @include hyphens(auto);

      a {
        color: black;
      }

      tr {
        padding: 10px;
      }
      .hover {
        cursor: pointer;
        @media #{$medium} {
          &:hover {
            $hover: var(--color-light-shade-3, $color-light-shade-3);
            background: $hover;
            td {
              border-bottom: 1px solid $hover;
              border-right: 4px solid $hover;
              border-left: 4px solid $hover;
              &:first-child {
                border-left: 0px;
                border-right: 4px solid $hover;
              }

              &:last-child {
                border-left: 4px solid $hover;
                border-right: 0px;
              }
            }
          }
        }
      }

      th {
        font-weight: 700;
        @include table-row-height;
        padding: 10px 40px;
        background: var(--color-light-shade-2, $color-light-shade-2);
        border-right: 4px solid white;
        border-left: 4px solid white;
        @include border-radius(10px 10px 0 0);

        &:first-child {
          border-left: 0px;
          border-right: 4px solid white;
        }

        &:last-child {
          border-left: 4px solid white;
          border-right: 0px;
        }

        .active .arrow {
          opacity: 1;
        }

        &.row {
          padding: 10px;
          justify-content: flex-end;
          border: 1px solid white;
          @include border-radius(10px 0 0 10px);
        }
      }

      .sort {
        display: block;
        width: 100%;
        cursor: pointer;
        @include text-select-none();
      }

      td {
        padding: 5px 10px 5px 10px;
        border-bottom: 1px solid var(--color-light-shade-3, $color-light-shade-3);
        border-right: 4px solid white;
        border-left: 4px solid white;
        @include table-row-height;

        &:first-child {
          border-left: 0px;
          border-right: 4px solid white;
        }

        &:last-child {
          border-left: 4px solid white;
          border-right: 0px;
        }

        &.link {
          text-align: center;
          color: var(--color-link, $color-link);
          cursor: pointer;
          text-decoration: none;
        }

        .active {
          color: var(--color-info-success, $color-info-success);
          font-weight: 700;

          &::before {
            @include small-circle-left($color-info-success);
          }
        }

        .inactive {
          color: var(--color-info-standard, $color-info-standard);
          font-weight: 700;

          &::before {
            @include small-circle-left($color-info-warning);
          }
        }

        .suspended {
          color: var(--color-info-danger, $color-info-danger);
          font-weight: 700;

          &::before {
            @include small-circle-left(var(--color-info-danger, $color-info-danger));
          }
        }

        &.corner {
          border-bottom: 0;
        }
      }

      .dean-tool-tip-content {
        padding: 3px;
      }
    }

    .action-icon,
    .download-icon {
      display: inline-block;
    }

    .disabled {
      cursor: default;
    }

    .center {
      @include display-flex();
      align-items: center;
      justify-content: center;
    }

    .left {
      @include display-flex();
      align-items: center;
      justify-content: flex-start;
    }

    .right {
      @include display-flex();
      align-items: center;
      justify-content: flex-end;
    }

    @media #{$small-only} {
      table {
        border: 0;
        //paddng: 0;

        thead {
          border: none;
          clip: rect(0 0 0 0);
          height: 1px;
          margin: -1px;
          padding: 0;
          position: absolute;
          width: 1px;
        }

        tr {
          display: block;
          padding: 0;
          margin-bottom: 20px;
          @include border-radius(10px);
          @include box-shadow(0px 3px 5px rgba(0, 0, 0, 0.075));
        }

        th {
          border-left: 0;
          border-right: 0;
          padding: 15px;
        }

        td {
          border-bottom: 1px solid var(--color-light-shade-3, $color-light-shade-3);
          display: flex;
          width: 100%;
          padding: 15px;
          border-left: 0;
          border-right: 0;
          text-align: right;
          max-width: 100%;
          justify-content: space-between;

          &:first-child {
            background: var(--color-light-shade-3, $color-light-shade-3);
            @include border-radius(10px 10px 0px 0px);
            border: 0;
            &:before {
              /** aria-label has no advantage, it won't be read inside a table
              content: attr(aria-label);*/
              content: attr(data-label);
              float: left;
              font-weight: 700;
            }
          }

          &:before {
            /** aria-label has no advantage, it won't be read inside a table
            content: attr(aria-label);*/
            content: attr(data-label);
            float: left;
          }

          &:last-child {
            border: 0;
            @include border-radius(0 0 10px 10px);
          }

        }

        .wrap {
          display: block;
          max-width: 150px;
          white-space: nowrap; /* Don't forget this one */
          text-overflow: ellipsis;
          overflow: hidden;
        }

        .loader {
          display: block;
          margin-right: 0;
          text-align: right;
        }
      }

      .action-icon,
      .download-icon {
        display: inline;
      }
    }
  }
</style>
